'use client';

import styled, { keyframes } from 'styled-components';
import Typography from '../Typography/Typography';
import Icon from '../Icon/Icon';
import Button from '../Button/Button';
import React, { ReactNode } from 'react';
import { toast as sonnerToast } from 'sonner';
import { ToastNotificationSonner, ToastVariant } from '@/hooks/useToast';
import { Flex3XS } from '@/components/Flex';

const ToastAnimation = keyframes`
    from {
        transform: translateX(-100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
`;

const Content = styled.div`
    display: grid;
    grid-template-columns: 1fr;
`;

const Title = styled(Typography)`
    font-weight: ${({ theme }) => theme.typography.weight.semiBold};
`;

const BaseDescription = styled(Typography)<{ $grey?: boolean }>`
  color: ${({
        theme,
        grey
    }) => grey ? theme.colors.palette.grey_600 : theme.colors.palette.grey_800};
`;

const AlignedDescription = styled(BaseDescription)<{ $grey?: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const Banner = styled.div<Partial<ToastProps> & { $title?: ReactNode, $variant?: ToastVariant }>`
    max-width: 768px;
    background-color: ${({
        theme,
        $variant
    }) => {
        if ($variant === 'success') {
            return theme.colors.palette.green_100;
        }
        if ($variant === 'error') {
            return '#FEE8E7';
        }
        return theme.colors.palette.white;
    }};

    ${Title} {
        color: ${({
        theme,
        $variant
    }) => {
        if ($variant === 'success' || $variant === 'error') {
            return theme.colors.palette.grey_900;
        }

        return theme.colors.palette.grey_800;
    }};
    }

    ${BaseDescription} {
        color: ${({
        theme,
        $title,
        $variant
    }) => {
        if ($variant === 'success' || $variant === 'error') {
            return theme.colors.palette.grey_900;
        }

        return $title ? theme.colors.palette.grey_600 : theme.colors.palette.grey_800;
    }};
    }

    border-radius: ${({ theme }) => theme.extras.radius.sm};
    border: ${({
        theme,
        $variant
    }) => {
        if ($variant === 'success') {
            return `1px solid ${theme.colors.palette.green_250}`;
        }
        if ($variant === 'error') {
            return `1px solid ${theme.colors.palette.red_200}`;
        }
        return 'none';
    }};
    box-shadow: ${({ theme }) => theme.colors.shadow.lg};
    padding: ${({
        theme,
        $title
    }) => $title ? theme.spacing['2xs'] : '8px 12px'};
    display: grid;
    grid-template-columns: 1fr minmax(24px, max-content);
    animation-name: ${ToastAnimation};
    animation-duration: 250ms;
`;

const Actions = styled.div<Partial<ToastProps>>`
    padding-left: 8px;
    margin-left: auto;
    margin-bottom: auto;
    ${({ button }) => button == 'primary-secondary-action' ? 'margin-top: auto;' : ''}
    height: ${({ button }) => button == 'primary-secondary-action' ? 'auto' : '2px'};
`;

const CloseIcon = styled(Icon)`
    color: ${({ theme }) => theme.colors.palette.grey_800};
`;

const VariantIcon = styled(Icon)<{ $variant?: string }>`
    color: ${({
        theme,
        $variant
    }) => {
        if ($variant === 'success') {
            return theme.colors.palette.green_300;
        }
        if ($variant === 'error') {
            return theme.colors.palette.red_300;
        }
        return theme.colors.palette.grey_800;
    }};
`;

const FlexFit3XS = styled(Flex3XS)<{
    $withoutDescription?: boolean;
}>`
    width: 100%;
    margin-bottom: ${({ $withoutDescription }) => $withoutDescription ? '0' : '6px'};
    align-items: center;
`;

const CommonButtonStyles = `
    cursor: pointer;
    color: ${({ theme }) => theme.colors.palette.grey_800};
`;

const GhostButton = styled(Button)`
    ${CommonButtonStyles};
    margin-left: ${({ button }) => button == 'ghost-text' ? '22px' : '24px'};
`;

const IconButton = styled(Button)`
    ${CommonButtonStyles};
    padding: 0 3px;
`;

const PositiveButton = styled(Button)`
    ${CommonButtonStyles};
    margin-left: 8px;
`;

const StrokedButton = styled(Button)`
    ${CommonButtonStyles}
`;

export function toast(payload: Partial<ToastNotificationSonner>) {
    return sonnerToast.custom((id) => (
        <Toast
          id={id}
          title={payload.title}
          variant={payload.variant}
          description={payload.description}
          button={payload.button}
          primaryAction={{
              btnText: payload.primaryAction?.btnText,
              func: payload.primaryAction?.func
          }}
          secondaryAction={{
              btnText: payload.secondaryAction?.btnText,
              func: payload.secondaryAction?.func
          }}
          handleClose={() => {
              payload?.handleClose && payload.handleClose();
              sonnerToast.dismiss(id);
          }}
          withDescriptionAlignment={payload.withDescriptionAlignment}
        />
    ), payload.sonner);
}

export default function Toast({
    title,
    description,
    button = 'ghost-icon',
    primaryAction,
    secondaryAction,
    handleClose,
    variant = 'normal',
    withDescriptionAlignment = true
}: Partial<ToastProps>) {
    const DescriptionComponent = withDescriptionAlignment ? AlignedDescription : BaseDescription;

    const handleButtonClick = (callback: Function | undefined | null) => {
        if (callback) {
            callback();
        }

        if (handleClose) {
            handleClose();
        }
    };

    const renderCloseIcon = () => (
        <IconButton
          startAdornment={<CloseIcon name='xmark-small-light' />}
          size='xs'
          variant='ghost'
          innerText=''
          onClick={handleClose}
        />
    );

    const renderCloseButton = () => (
        <GhostButton variant='ghost' innerText='Discard' size='xs' onClick={handleClose} button={button} />
    );

    const renderActionWithCancelButtons = () => (
        <>
            <GhostButton
              variant='ghost'
              innerText={secondaryAction?.btnText}
              onClick={() => handleButtonClick(secondaryAction?.func)}
              size='sm'
              button={button}
            />
            <PositiveButton
              innerText={primaryAction?.btnText}
              onClick={() => handleButtonClick(primaryAction?.func)}
              size='sm'
            />
        </>
    );

    const renderButtons = () => {
        switch (button) {
            case 'ghost-text':
                return renderCloseButton();
            case 'primary-secondary-action':
                return renderActionWithCancelButtons();
            case 'ghost-icon':
            case 'secondary-action':
            default:
                return renderCloseIcon();
        }
    };

    const renderVariantIcon = () => {
        if (variant === 'success') {
            return <VariantIcon name='checkmark-double-light' $variant={variant} />;
        }
        if (variant === 'error') {
            return <VariantIcon name='alert-triangle-light' $variant={variant} />;
        }

        return null;
    };

    return (
        <Banner $title={title} button={button} $variant={variant} role='alert'>
            <Content>
                {title && (
                    <FlexFit3XS $withoutDescription={!description}>
                        {(variant === 'success' || variant === 'error') && renderVariantIcon()}

                        <Title variant='uiType' size='sm' modifier='R'>
                            {title}
                        </Title>
                    </FlexFit3XS>
                )}
                {description &&
                    <DescriptionComponent variant='uiType' size={variant === 'error' || variant === 'success' ? 'xs' : 'sm'} modifier={title ? 'R' : 'S'} $grey={!!title}>
                        {description}
                    </DescriptionComponent>
                }
                {button == 'secondary-action' && secondaryAction &&
                    <div>
                        <StrokedButton
                          variant='stroke'
                          innerText={secondaryAction.btnText}
                          onClick={() => handleButtonClick(secondaryAction?.func)}
                          size='xs'
                        />
                    </div>
                }
            </Content>

            <Actions button={button}>
                {renderButtons()}
            </Actions>
        </Banner>
    );
}

export type ToastProps = {
    id: string | number,
    title?: ReactNode,
    description?: ReactNode,
    button: string,
    primaryAction?: {
        btnText?: string,
        func?: Function,
    },
    secondaryAction?: {
        btnText?: string,
        func?: Function,
    },
    handleClose?: Function,
    variant?: ToastVariant,
    withDescriptionAlignment?: boolean,
}
